import { useState, useEffect } from 'react';
import { EventConfig } from '@/types/event.types';
import { EventConfigService } from '@/services/EventConfigService';
import { StaticEventConfigService } from '@/services/StaticEventConfigService';

/**
 * Hook to load event configuration on mount
 * Supports both backend API mode and static demo mode
 * @param eventSlug - Event slug to load (defaults to 'mock-event-1')
 * @returns Event config, loading state, and error
 */
export function useEventConfig(eventSlug: string = 'mock-event-1') {
  const [config, setConfig] = useState<EventConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if we're in demo mode
        const isDemoMode = import.meta.env.VITE_DEMO_MODE === 'true';

        // Use appropriate service based on mode
        const service = isDemoMode
          ? new StaticEventConfigService()
          : new EventConfigService();

        const eventConfig = await service.getConfig(eventSlug);

        setConfig(eventConfig);
        console.log('[useEventConfig] Event config loaded', {
          mode: isDemoMode ? 'demo' : 'backend',
          eventSlug,
          eventName: eventConfig.event.name,
        });
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to load event config');
        setError(error);
        console.error('[useEventConfig] Failed to load config', { eventSlug, error });
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [eventSlug]);

  return { config, loading, error };
}
